import type { Env, FilterValue, ResourceCard, SortValue } from "./types";
import { RESOURCE_TYPES } from "./types";
import {
  FILTER_SLUG,
  TYPE_BY_FILTER_SLUG,
  getCollectionId,
  typesForFilter,
} from "./collections";
import { fetchAllItems } from "./webflow-cdn";
import { mapResourceItem } from "./card-mapper";
import { compareResources, makeResourceComparator } from "./sort";

/**
 * SSR entry point: fetch all four collections in parallel, slim each item to a
 * `ResourceCard`, and sort. Responses are edge-cached by Webflow (~300s), so
 * this stays cheap under load.
 *
 * One unreachable collection fails the whole page rather than silently
 * rendering a partial grid — a resources page missing a quarter of its content
 * is harder to notice, and worse, than one that errors loudly.
 */
export async function fetchAllResourceCards(env: Env): Promise<ResourceCard[]> {
  const perCollection = await Promise.all(
    RESOURCE_TYPES.map(async (type) => {
      const id = getCollectionId(type, env);
      const items = await fetchAllItems(env, id);
      return items.map((item) => mapResourceItem(item, type));
    }),
  );

  return perCollection.flat().sort(compareResources);
}

const SEARCHABLE_FIELDS = ["title", "description", "typeLabel"] as const;

/** Client-side filter + search. Runs on the already-fetched card array. */
export function filterCards(
  cards: ResourceCard[],
  filter: FilterValue,
  query: string,
): ResourceCard[] {
  const q = query.trim().toLowerCase();
  const allowedTypes = typesForFilter(filter);

  return cards.filter((card) => {
    if (allowedTypes !== "all" && !allowedTypes.includes(card.type)) {
      return false;
    }
    if (q.length === 0) return true;
    return SEARCHABLE_FIELDS.some((field) =>
      card[field].toLowerCase().includes(q),
    );
  });
}

/**
 * Re-sort an already-filtered set. SSR sorts once with the default direction;
 * this runs client-side when a visitor changes it. Returns a new array — the
 * caller's input is memoised upstream and must not be mutated.
 */
export function sortCards(
  cards: ResourceCard[],
  sort: SortValue,
): ResourceCard[] {
  return [...cards].sort(makeResourceComparator(sort));
}

const VALID_FILTERS: ReadonlyArray<FilterValue> = [
  "all",
  ...RESOURCE_TYPES,
] as const;

/**
 * Public filter slug → internal `FilterValue`. Unknown input falls back to
 * "all".
 *
 * Accepts BOTH vocabularies. The public one is plural (`webinars`,
 * `analyst-reports`) and is what `?type=` and `setFilter()` use; the internal
 * one is singular (`webinar`, `report`). Continuing to accept the singular form
 * costs one lookup and keeps every link already shared as
 * `/resources?type=webinar` working, rather than silently resetting those
 * visitors to the unfiltered grid.
 */
export function parseFilterValue(input: string | undefined): FilterValue {
  if (!input) return "all";
  if (input === "all") return "all";

  const fromSlug = TYPE_BY_FILTER_SLUG[input];
  if (fromSlug) return fromSlug;

  // Legacy singular form.
  if ((VALID_FILTERS as readonly string[]).includes(input)) {
    return input as FilterValue;
  }
  return "all";
}

/**
 * Internal `FilterValue` → the public slug. The inverse of
 * `parseFilterValue`, and the only thing that should ever be written to the URL
 * or reported on `resources-embed:change`.
 */
export function filterToSlug(filter: FilterValue): string {
  return filter === "all" ? "all" : FILTER_SLUG[filter];
}

/**
 * Search text out of a query string.
 *
 * The parameter is `search`. `q` was its previous name and is still read, so
 * links already shared as `?q=…` keep working; `search` wins if a URL somehow
 * carries both. Only `search` is ever written back — see the URL mirror in
 * `ResourcesIsland`.
 */
export function parseSearchParam(params: URLSearchParams): string {
  return params.get("search") ?? params.get("q") ?? "";
}

const VALID_SORTS: ReadonlyArray<SortValue> = ["latest", "oldest"] as const;

/**
 * `?sort=` → a safe `SortValue`. Unknown input falls back to "latest".
 *
 * Deliberately forgiving rather than throwing: this parses values that reach it
 * from a query string and from `window.ResourcesEmbed.setSort()`, i.e. from
 * hand-written custom code on a Webflow page. A typo there should leave the
 * grid in its default order, not break it.
 */
export function parseSortValue(input: string | undefined): SortValue {
  if (!input) return "latest";
  if ((VALID_SORTS as readonly string[]).includes(input)) {
    return input as SortValue;
  }
  return "latest";
}
